import type { Table } from '@tanstack/react-table'
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import { IconButton } from '../IconButton.tsx'

interface Props<TRow> {
  table: Table<TRow>
  pageSizeOptions?: number[]
  /** Total count from server (overrides client-side row count). */
  totalRowCount?: number
}

export function DataTablePagination<TRow>({ table, pageSizeOptions = [10, 25, 50, 100], totalRowCount }: Props<TRow>) {
  const { pagination } = table.getState()
  const pageCount = table.getPageCount()
  const pageIndex = pagination.pageIndex
  const effectiveTotal = totalRowCount ?? table.getFilteredRowModel().rows.length
  const start = effectiveTotal === 0 ? 0 : pageIndex * pagination.pageSize + 1
  const end = Math.min(effectiveTotal, (pageIndex + 1) * pagination.pageSize)

  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2 border-t border-gray-200 bg-white text-small text-gray-600 flex-wrap">
      <div className="flex items-center gap-2">
        <span>
          {start}–{end} von {effectiveTotal}
        </span>
      </div>
      <div className="flex items-center gap-3">
        {totalRowCount === undefined && (
          <div className="flex items-center gap-1">
            <span className="text-caption text-gray-500">Zeilen pro Seite:</span>
            <select
              value={pagination.pageSize}
              onChange={(e) => table.setPageSize(Number(e.target.value))}
              className="rounded border border-gray-300 bg-white py-0.5 pl-1.5 pr-6 text-small focus:outline-none focus:ring-2 focus:ring-brand-400/30"
            >
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>
        )}
        <div className="flex items-center gap-1">
          <IconButton
            size="sm"
            icon={<ChevronsLeft />}
            aria-label="Erste Seite"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
          />
          <IconButton
            size="sm"
            icon={<ChevronLeft />}
            aria-label="Vorige Seite"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
          />
          <span className="text-caption text-gray-500 px-1">
            Seite {pageIndex + 1} / {Math.max(1, pageCount)}
          </span>
          <IconButton
            size="sm"
            icon={<ChevronRight />}
            aria-label="Nächste Seite"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
          />
          <IconButton
            size="sm"
            icon={<ChevronsRight />}
            aria-label="Letzte Seite"
            onClick={() => table.setPageIndex(pageCount - 1)}
            disabled={!table.getCanNextPage()}
          />
        </div>
      </div>
    </div>
  )
}
